import * as React from 'react';
import { Badge, Box, Button, CollectionPreferences, CollectionPreferencesProps, ContentLayout, Header, Modal, Pagination, PropertyFilter, SpaceBetween, Table, TextFilter } from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { useChallenges } from '@/src/store/challenge.context';
import { useEffect, useLayoutEffect, useState } from 'react';
import { ChallengeSnippet } from '@/src/types/ChallengeSnippets';
import { PropertyFilterProperty, useCollection } from '@amzn/awsui-collection-hooks';
import { callIgnoringExceptionsAsync } from '@/src/utils/call-ignoring-exceptions';
import { ChallengeCFN, Snippets } from '@/src/types/ChallengeSet';
import { useApi } from '@/src/store/api.context';
import JamCodeEditor from '../../challengesCommon/JamCodeEditor';
import { preProdLogger } from '@/src/utils/log.utils';
import { UserPreferenceKeys } from '@/src/store/user.context';
import { TableEmptyState } from '@/src/components/common/TableEmptyState';

interface SmartTextI {
  text: string;
  length: number;
}

const ChallengeSnippets: React.FC = () => {
  const { challengeCFN } = useChallenges();
  const [challengeSnippets, setChallengeSnippets] = useState<ChallengeSnippet[]>([]);
  const [previewVisible, setPreviewVisible] = useState<boolean>(false);
  const [snippetsMarkdown, setSnippetsMarkdown] = useState<string>('');
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: 10,
  });
  const { challengesApi } = useApi();
  const { t } = useTranslation();

  useLayoutEffect(() => {
    if (!challengeCFN) {
      void getALLCFN();
    }
  }, []);

  const getALLCFN = async () => {
    await challengesApi.getAllCFN().then(res => {
      const snippets = res.map(item => item.sharableSnippet as unknown as ChallengeSnippet);
      if (snippets) {
        setChallengeSnippets(snippets);
      }
    });
  }

  useEffect(() => {
    const snippets = challengeCFN?.map(item => item.sharableSnippet as unknown as ChallengeSnippet);
    if (snippets) {
      setChallengeSnippets(snippets);
    }
  }, []);

  const filteringProperties: PropertyFilterProperty[] = [
    {
      key: 'title',
      operators: ['=', ':'],
      propertyLabel: 'Title',
      groupValuesLabel: 'title',
    },
    {
      key: 'tags',
      operators: ['=', ':'],
      propertyLabel: 'Tag',
      groupValuesLabel: 'tags',
    },
  ]

  const { items, collectionProps, paginationProps, propertyFilterProps } =
    useCollection(challengeSnippets, {
      // TODO: Filtering messages here not applying to the Table
      filtering: {
        empty: (
          <TableEmptyState
            title={t(i18nKeys.eventTemplates.customTable.empty.noTitle, { headerTitle: t(i18nKeys.challenges.subSections.cfnTemplate.challengeSnippts.snippets) })}
            subtitle={t(i18nKeys.eventTemplates.customTable.empty.noSubTitle, { headerTitle: t(i18nKeys.challenges.subSections.cfnTemplate.challengeSnippts.snippets) })}
          />
        ),
        noMatch: (
          <TableEmptyState
            title={t(i18nKeys.eventTemplates.customTable.noMatch.title)}
            subtitle={t(i18nKeys.eventTemplates.customTable.noMatch.subTitle)}
          />
        ),
      },
      pagination: { pageSize: preferences.pageSize },
      propertyFiltering: {
        filteringProperties,
        defaultQuery: {
          tokens: [],
          operation: "and"
        }
      },
      sorting: {},
      selection: {}
    });

  const handleSetPreferences = (tablePreferences: CollectionPreferencesProps.Preferences) => {
    if (tablePreferences.pageSize) {
      sessionStorage.setItem(UserPreferenceKeys.PAGE_SIZE, tablePreferences.pageSize.toString());
    }
    setPreferences(tablePreferences);
  };

  const SmartText: React.FC<SmartTextI> = ({ text, length = 20 }) => {
    const [showLess, setShowLess] = React.useState(true);

    if (text?.length < length) {
      return <p>{text}</p>;
    }

    return (
      <div>
        <p>{showLess ? `${text?.slice(0, length)}` : text}</p>
        <Button variant='inline-link' onClick={() => setShowLess(!showLess)} data-testid="toggle-desc-button">
          {showLess
            ? t(i18nKeys.challenges.subSections.cfnTemplate.challengeSnippts.showDescription)
            : t(i18nKeys.challenges.subSections.cfnTemplate.challengeSnippts.hideDescription)}
        </Button>
      </div>
    );
  };

  const getDescriptions = (item: ChallengeSnippet) => {
    if (item.description && item?.description.length > 1) {
      return <SmartText text={item.description} length={0} />;
    }
  }

  const getSnippet = (id: string) => {
    void callIgnoringExceptionsAsync(async () => {
      return await challengesApi.getSnippets(id);
    }).then((challengeCFNResponse: ChallengeCFN | undefined) => {
      if (challengeCFNResponse) {
        setSnippetsMarkdown((challengeCFNResponse.sharableSnippet as unknown as Snippets).snippet);
        setPreviewVisible(true);
      }
    });
  }

  const onPreview = (item: ChallengeSnippet) => {
    getSnippet(item.id || '');
  }

  return (
    <ContentLayout header={
      <Header variant="h1">
        {t(i18nKeys.challenges.subSections.cfnTemplate.snippetTitle, { count: challengeSnippets.length })}
      </Header>
    }>
      <Box>
        <Table
          {...collectionProps}
          ariaLabels={{
            selectionGroupLabel: t(i18nKeys.challenges.selectionGroupLabel),
            allItemsSelectionLabel: ({ selectedItems }) =>
              `${selectedItems.length} ${selectedItems.length === 1 ? "item" : "items"
              } selected`,
            itemSelectionLabel: ({ }, item: ChallengeSnippet) =>
              item.title || ''
          }}
          columnDefinitions={[
            {
              id: "title",
              header: t(i18nKeys.challenges.subSections.cfnTemplate.challengeSnippts.title),
              cell: item => <SpaceBetween direction="horizontal" size={'s'}>{item.title}</SpaceBetween>,
              sortingField: "title",
              isRowHeader: true
            },
            {
              id: "preview",
              header: t(i18nKeys.challenges.subSections.cfnTemplate.challengeSnippts.preview),
              cell: (item => <Button data-testid='challenge-snippet-preview' variant="link" onClick={() => onPreview(item)}>{t(i18nKeys.challenges.subSections.cfnTemplate.challengeSnippts.preview)}</Button>),
              sortingField: "preview"
            },
            {
              id: "tags",
              header: t(i18nKeys.challenges.subSections.cfnTemplate.challengeSnippts.tags),
              cell: item => (<SpaceBetween direction="horizontal" size="xxs">{item.tags?.map((tag, index) => <Badge key={index}>{tag}</Badge>)}{''}</SpaceBetween>)
            },
            {
              id: "description",
              header: t(i18nKeys.challenges.subSections.cfnTemplate.challengeSnippts.description),
              cell: item => getDescriptions(item),
              sortingField: "description"
            },
            {
              id: "rating",
              header: t(i18nKeys.challenges.subSections.cfnTemplate.challengeSnippts.rating),
              cell: item => <>{item.positiveFeedbackNum} {t(i18nKeys.challenges.subSections.cfnTemplate.challengeSnippts.vote)}</>,
              sortingField: "rating"
            }
          ]}
          columnDisplay={[
            { id: "title", visible: true },
            { id: "preview", visible: true },
            { id: "tags", visible: true },
            { id: "description", visible: true },
            { id: "rating", visible: true }
          ]}
          items={items}
          loadingText="Loading resources"
          trackBy="title"
          resizableColumns
          empty={
            <Box
              margin={{ vertical: "xs" }}
              textAlign="center"
              color="inherit"
            >
              <SpaceBetween size="m">
                <b>{t(i18nKeys.eventTemplates.customTable.empty.noTitle, { headerTitle: t(i18nKeys.challenges.subSections.cfnTemplate.challengeSnippts.snippets) })}</b>
              </SpaceBetween>
            </Box>
          }
          pagination={
            <Pagination
              ariaLabels={{
                nextPageLabel: t(i18nKeys.challenges.nextPageLabel),
                paginationLabel: t(i18nKeys.challenges.paginationLabel),
                previousPageLabel: t(i18nKeys.challenges.previousPageLabel),
                pageLabel: (pageNumber) => `page ${pageNumber}`,
              }}
              {...paginationProps}
            />
          }
          filter={
            <Box margin={{ top: 'xs', bottom: 'xs' }}>
              <PropertyFilter
                {...propertyFilterProps}
                i18nStrings={{
                  filteringAriaLabel: t(i18nKeys.eventTemplates.customTable.filter.filteringAriaLabel),
                  dismissAriaLabel: t(i18nKeys.eventTemplates.customTable.filter.dismissAriaLabel),
                  filteringPlaceholder: t(i18nKeys.challenges.subSections.cfnTemplate.challengeSnippts.snippetsPlaceholder),
                  groupValuesText: t(i18nKeys.eventTemplates.customTable.filter.groupValuesText),
                  groupPropertiesText: t(i18nKeys.eventTemplates.customTable.filter.groupPropertiesText),
                  operatorsText: t(i18nKeys.eventTemplates.customTable.filter.operatorsText),
                  operationAndText: t(i18nKeys.eventTemplates.customTable.filter.operationAndText),
                  operatorContainsText: t(i18nKeys.eventTemplates.customTable.filter.operatorContainsText),
                  operatorEqualsText: t(i18nKeys.eventTemplates.customTable.filter.operatorEqualsText),
                  editTokenHeader: t(i18nKeys.eventTemplates.customTable.filter.editTokenHeader),
                  propertyText: t(i18nKeys.eventTemplates.customTable.filter.propertyText),
                  operatorText: t(i18nKeys.eventTemplates.customTable.filter.operatorText),
                  valueText: t(i18nKeys.eventTemplates.customTable.filter.valueText),
                  cancelActionText: t(i18nKeys.eventTemplates.customTable.filter.cancelActionText),
                  applyActionText: t(i18nKeys.eventTemplates.customTable.filter.applyActionText),
                  allPropertiesLabel: t(i18nKeys.eventTemplates.customTable.filter.allPropertiesLabel),
                  tokenLimitShowMore: t(i18nKeys.eventTemplates.customTable.filter.tokenLimitShowMore),
                  tokenLimitShowFewer: t(i18nKeys.eventTemplates.customTable.filter.tokenLimitShowFewer),
                  clearFiltersText: t(i18nKeys.eventTemplates.customTable.filter.clearFiltersText),
                }}
              />
            </Box>
          }
          preferences={
            <CollectionPreferences
              title={t(i18nKeys.events.fields.preferences.title)}
              onConfirm={({ detail }) => handleSetPreferences(detail)}
              confirmLabel={t(i18nKeys.general.confirm)}
              cancelLabel={t(i18nKeys.general.cancel)}
              preferences={preferences}
              pageSizePreference={{
                title: t(i18nKeys.challenges.subSections.cfnTemplate.challengeSnippts.snippets),
                options: [
                  { value: 10, label: t(i18nKeys.challenges.subSections.cfnTemplate.challengeSnippts.snippets, { count: 10 }) },
                  { value: 20, label: t(i18nKeys.challenges.subSections.cfnTemplate.challengeSnippts.snippets, { count: 20 }) },
                  { value: 40, label: t(i18nKeys.challenges.subSections.cfnTemplate.challengeSnippts.snippets, { count: 40 }) },
                ]
              }}
            />
          }
          stickyColumns={{ first: 0, last: 1 }}
        />
        <Modal
          visible={previewVisible}
          onDismiss={() => setPreviewVisible(false)}
          data-testid="preview-snippets-modal"
        >
          <div>
            <JamCodeEditor
              language="yaml"
              value={snippetsMarkdown}
              setValue={() => {
                preProdLogger('none');
              }}
            />
          </div>
        </Modal>
      </Box>
    </ContentLayout>
  );
};

export default ChallengeSnippets;
